import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { MatCardModule } from '@angular/material/card';
import { LoggerService } from '../../../../core/services/logger.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, take } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Parameter,
  ParameterDetail,
  CreateParameterRequest
} from '../../models/parameter.interface';
import { PARAMETER_LABELS } from './parameter.labels';
import * as ParameterActions from '../../store/parameter.actions';
import * as ParameterSelectors from '../../store/parameter.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';
import { NotificationService } from '../../../../shared/services/notification.service';

@Component({
  selector: 'app-parameter',
  templateUrl: './parameter.component.html',
  styleUrls: ['./parameter.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    MatExpansionModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatPaginatorModule
  ]
})
export class ParameterComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly logger = inject(LoggerService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();

  readonly labels = PARAMETER_LABELS;

  parameterList$ = this.store.select(ParameterSelectors.selectAllParameters);
  selectedParameter$ = this.store.select(ParameterSelectors.selectSelectedParameter);
  parameterDetailList$ = this.store.select(ParameterSelectors.selectParameterDetails);
  loading$ = this.store.select(ParameterSelectors.selectParameterLoading);
  error$ = this.store.select(ParameterSelectors.selectParameterError);
  creating$ = this.store.select(ParameterSelectors.selectParameterCreating);
  updating$ = this.store.select(ParameterSelectors.selectParameterUpdating);

  // Observable combinado para mostrar parámetros o resultados de búsqueda
  displayParameters$ = this.parameterList$.pipe(
    map(parameters => {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return parameters;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return parameters.filter(
        parameter =>
          parameter.vcNombre.toLowerCase().includes(query) ||
          parameter.vcCodigoInterno.toLowerCase().includes(query)
      );
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  parameterForm!: FormGroup;
  isCreatingDetail = false;
  isCreatingParent = false;
  parameterDetailInEdit: ParameterDetail | null = null;
  searchQuery = '';
  showSearchPanel = false;

  tableColumns: string[] = ['code', 'name', 'status', 'actions'];

  detailBeingViewed: ParameterDetail | Parameter | null = null;
  isParentDetail = false;

  @ViewChild('createParameterModal') createParameterModal!: TemplateRef<unknown>;
  @ViewChild('detailModal') detailModal!: TemplateRef<unknown>;
  @ViewChild('paginator') paginator!: MatPaginator;

  readonly trackByParameterDetailId = TrackByUtils.trackById;

  ngOnInit(): void {
    this.initializeForm();
    this.loadParameters();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.parameterForm = this.formBuilder.group({
      code: ['', Validators.required],
      name: ['', Validators.required]
    });
  }

  public loadParameters(): void {
    this.store.dispatch(ParameterActions.loadAllParameters());
  }

  private setupSubscriptions(): void {
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error, 'Parameter Component');
      }
    });

    // Listen for successful parameter creation to ensure UI updates
    this.store
      .select(ParameterSelectors.selectParameterCreating)
      .pipe(takeUntil(this.destroy$))
      .subscribe(creating => {
        if (!creating) {
          // Parameter creation finished, the effect will automatically reload the list
          this.logger.info('Parameter creation completed');
        }
      });
  }

  onParameterSelectionChange(parameter: Parameter): void {
    this.store.dispatch(ParameterActions.selectParameter({ parameter }));
  }

  /**
   * Alterna el panel de búsqueda
   */
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
    if (!this.showSearchPanel) {
      this.clearSearch();
    }
  }

  /**
   * Busca parámetros
   */
  searchParameters(query: string): void {
    this.searchQuery = query;
  }

  /**
   * Limpia la búsqueda
   */
  clearSearch(): void {
    this.searchQuery = '';
  }

  openModal(modalType: 'parent' | 'child' | ParameterDetail): void {
    this.parameterForm.reset();

    if (modalType === 'parent') {
      this.isCreatingDetail = false;
      this.isCreatingParent = true;
      this.parameterDetailInEdit = null;
    } else if (modalType === 'child') {
      this.isCreatingDetail = true;
      this.isCreatingParent = false;
      this.parameterDetailInEdit = null;
    } else {
      this.isCreatingDetail = true;
      this.isCreatingParent = false;
      this.parameterDetailInEdit = modalType;
      this.parameterForm.setValue({
        code: modalType.dCodigoIterno.toString(),
        name: modalType.vcNombre
      });
    }

    this.dialog.open(this.createParameterModal);
  }

  closeModal(): void {
    this.dialog.closeAll();
  }

  async saveParameter(): Promise<void> {
    if (this.parameterForm.invalid) {
      return;
    }

    const codeValue = this.parameterForm.get('code')!.value;
    const nameValue = this.parameterForm.get('name')!.value;

    // Mostrar confirmación antes de guardar
    const confirmed = await this.notificationService.showConfirmation(
      `¿Está seguro de que desea ${this.parameterDetailInEdit ? 'actualizar' : 'crear'} este parámetro?`,
      'Confirmar Operación'
    );

    if (confirmed) {
      if (this.parameterDetailInEdit) {
        this.updateParameterDetail(codeValue, nameValue);
        setTimeout(() => {
          this.notificationService.showSuccessModal('Parámetro actualizado correctamente');
        }, 500);
      } else if (this.isCreatingDetail) {
        this.createParameterDetail(codeValue, nameValue);
        setTimeout(() => {
          this.notificationService.showSuccessModal('Parámetro creado correctamente');
        }, 500);
      } else if (this.isCreatingParent) {
        this.createParentParameter(codeValue, nameValue);
        setTimeout(() => {
          this.notificationService.showSuccessModal('Parámetro creado correctamente');
        }, 500);
      } else {
        this.updateParentParameter(codeValue, nameValue);
        setTimeout(() => {
          this.notificationService.showSuccessModal('Parámetro actualizado correctamente');
        }, 500);
      }

      this.closeModal();
    }
  }

  private updateParameterDetail(code: string, name: string): void {
    if (this.parameterDetailInEdit) {
      this.selectedParameter$.pipe(take(1), takeUntil(this.destroy$)).subscribe(parameter => {
        if (parameter) {
          const detail: Partial<ParameterDetail> = {
            vcCodigoInterno: code,
            vcNombre: name
          };
          this.store.dispatch(
            ParameterActions.updateParameterDetail({
              parameterId: parameter.idParametro,
              detailId: this.parameterDetailInEdit!.idParametroDetalle,
              detail
            })
          );
        }
      });
    }
  }

  private createParameterDetail(code: string, name: string): void {
    this.selectedParameter$.pipe(take(1), takeUntil(this.destroy$)).subscribe(parameter => {
      if (parameter) {
        const detail: Partial<ParameterDetail> = {
          vcNombre: name,
          txDescripcion: name,
          vcCodigoInterno: code,
          dCodigoIterno: 0,
          rangoDesde: 0,
          rangoHasta: 0
        };
        this.store.dispatch(
          ParameterActions.createParameterDetail({
            parameterId: parameter.idParametro,
            detail
          })
        );
      }
    });
  }

  private createParentParameter(code: string, name: string): void {
    const newParameter: CreateParameterRequest = {
      VcNombre: name,
      VcCodigoInterno: code
    };
    this.store.dispatch(ParameterActions.createParameter({ request: newParameter }));
  }

  private updateParentParameter(code: string, name: string): void {
    // Update an existing parameter
    this.selectedParameter$.pipe(take(1), takeUntil(this.destroy$)).subscribe(parameter => {
      if (parameter) {
        const updateData = {
          idParametro: parameter.idParametro,
          vcNombre: name,
          vcCodigoInterno: code,
          BEstado: parameter.bEstado
        };
        this.store.dispatch(
          ParameterActions.updateParameter({ id: parameter.idParametro, request: updateData })
        );
      }
    });
  }

  viewDetail(item: ParameterDetail | Parameter, isParent: boolean): void {
    this.detailBeingViewed = item;
    this.isParentDetail = isParent;
    this.dialog.open(this.detailModal);
  }

  async onStatusChange(element: ParameterDetail, newStatus: boolean): Promise<void> {
    const confirmed = await this.notificationService.showConfirmation(
      `¿Está seguro de que desea ${newStatus ? 'activar' : 'desactivar'} este parámetro?`,
      'Confirmar Cambio de Estado'
    );

    if (confirmed) {
      element.bEstado = newStatus;
      setTimeout(() => {
        this.notificationService.showSuccessModal(
          `Parámetro ${newStatus ? 'activado' : 'desactivado'} correctamente`
        );
      }, 500);
    }
  }

  getParameterName(): string {
    let name = '';
    this.selectedParameter$.pipe(take(1), takeUntil(this.destroy$)).subscribe(parameter => {
      name = parameter?.vcNombre || '';
    });
    return name;
  }

  trackByParameterId(index: number, parameter: Parameter): number {
    return parameter.idParametro;
  }

  // Cache management methods
  invalidateCache(): void {
    this.store.dispatch(ParameterActions.invalidateCache());
  }

  forceRefresh(): void {
    this.store.dispatch(ParameterActions.forceRefresh());
    this.loadParameters();
  }

  clearCache(): void {
    this.store.dispatch(ParameterActions.clearCache());
  }
}
